import { Link as RouterLink } from "react-router-dom";
import {
    alpha,
    Avatar,
    Box,
    CircularProgress,
    Divider,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    Paper,
    Typography,
} from "@mui/material";
import AnnouncementOutlinedIcon from "@mui/icons-material/AnnouncementOutlined";
import ReportOutlinedIcon from "@mui/icons-material/ReportOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { formatRelativeTime } from "../utils/formatRelativeTime";

const TYPE_ICONS = {
    notice: AnnouncementOutlinedIcon,
    complaint: ReportOutlinedIcon,
    fee: PaymentsOutlinedIcon,
    payment: PaymentsOutlinedIcon,
    marks: MenuBookOutlinedIcon,
    attendance: EventNoteOutlinedIcon,
    scheme: EventNoteOutlinedIcon,
    leave: SupervisorAccountOutlinedIcon,
    teacher: SupervisorAccountOutlinedIcon,
    class: ClassOutlinedIcon,
    expense: PaymentsOutlinedIcon,
    resource: InsertDriveFileOutlinedIcon,
};

function ActivityIcon({ type, tone }) {
    const Icon = TYPE_ICONS[type] || HistoryOutlinedIcon;
    return (
        <Avatar
            sx={{
                bgcolor: (t) => alpha(t.palette[tone]?.main || t.palette.primary.main, 0.12),
                color: `${tone}.main`,
                width: 40,
                height: 40,
            }}
        >
            <Icon fontSize="small" />
        </Avatar>
    );
}

const RecentActivityPanel = ({ activities = [], loading = false, error = null, sx = {} }) => {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 3,
                height: 360,
                display: "flex",
                flexDirection: "column",
                border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                ...sx,
            }}
        >
            <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                Recent activity
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Live updates from your school data
            </Typography>
            <Divider />
            <Box sx={{ flex: 1, overflow: "auto", mt: 0.5 }}>
                {loading ? (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", minHeight: 200 }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : error ? (
                    <Typography variant="body2" color="error" sx={{ py: 3, px: 1 }}>
                        {error}
                    </Typography>
                ) : activities.length === 0 ? (
                    <Box sx={{ py: 4, px: 1, textAlign: "center" }}>
                        <HistoryOutlinedIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                            No recent activity yet. Events will appear here as notices, fees, submissions, and complaints are recorded.
                        </Typography>
                    </Box>
                ) : (
                    <List dense disablePadding>
                        {activities.map((item) => {
                            const content = (
                                <>
                                    <ListItemAvatar sx={{ minWidth: 52 }}>
                                        <ActivityIcon type={item.type} tone={item.tone || "primary"} />
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={item.primary}
                                        secondary={`${item.secondary || "School"} · ${formatRelativeTime(item.occurredAt)}`}
                                        primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                                        secondaryTypographyProps={{ variant: "caption" }}
                                    />
                                </>
                            );

                            if (item.link) {
                                return (
                                    <ListItem key={item.id} disablePadding alignItems="flex-start">
                                        <ListItemButton component={RouterLink} to={item.link} sx={{ py: 1.25, px: 0, borderRadius: 1.5 }}>
                                            {content}
                                        </ListItemButton>
                                    </ListItem>
                                );
                            }

                            return (
                                <ListItem key={item.id} alignItems="flex-start" sx={{ px: 0, py: 1.25 }}>
                                    {content}
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </Box>
        </Paper>
    );
};

export default RecentActivityPanel;
